import type {
  HelpCenterAudience,
  IHelpCenter,
  IHelpCenterAudiencePage,
  IHelpCenterLink,
  IHelpCenterMediaResource,
  IHelpCenterSection,
  IHelpCenterSubSection,
} from "../../interfaces/models/IHelpCenter";

function createHelpCenterId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const HELP_CENTER_BROWSE_BY_TOPIC_TEXT =
  "Click on the links below to access resources such as help guides, downloadable PDFs, videos and more to aid in getting started or learning more about the WSG website.";

export const DEFAULT_VISIBLE_LINK_COUNT = 4;

export function createEmptyHelpCenterSection(sortOrder: number): IHelpCenterSection {
  const emptyLink: IHelpCenterLink = {
    id: createHelpCenterId("link"),
    label: "",
    url: "",
    sortOrder: 1,
    isPublished: true,
    openInNewTab: false,
  };

  return {
    id: createHelpCenterId("section"),
    title: "",
    sortOrder,
    isPublished: true,
    maxVisibleLinks: DEFAULT_VISIBLE_LINK_COUNT,
    links: [emptyLink],
    subSections: [],
  };
}

export const HELP_CENTER_AUDIENCE_LABELS: Record<HelpCenterAudience, string> = {
  members: "Members",
  dataAdmin: "Data Admin",
  staff: "Staff",
};

export const HELP_CENTER_AUDIENCE_OPTIONS: Array<{
  id: HelpCenterAudience;
  name: string;
}> = [
  { id: "members", name: HELP_CENTER_AUDIENCE_LABELS.members },
  { id: "dataAdmin", name: HELP_CENTER_AUDIENCE_LABELS.dataAdmin },
  { id: "staff", name: HELP_CENTER_AUDIENCE_LABELS.staff },
];

export function sortByOrder<T extends { sortOrder: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getAudiencePage(
  config: IHelpCenter,
  audience: HelpCenterAudience,
): IHelpCenterAudiencePage | undefined {
  const page = config[audience];
  if (!page || !Array.isArray(page.sections)) {
    return undefined;
  }
  return page;
}

export function getPublishedSubSections(
  section: IHelpCenterSection,
): IHelpCenterSubSection[] {
  const subSections = Array.isArray(section.subSections)
    ? section.subSections
    : [];

  return sortByOrder(
    subSections.filter(
      (subSection) =>
        subSection.isPublished && subSection.title.trim().length > 0,
    ),
  );
}

export function findHelpCenterSection(
  config: IHelpCenter,
  audience: HelpCenterAudience,
  sectionId: string,
): IHelpCenterSection | undefined {
  return config[audience].sections.find((section) => section.id === sectionId);
}

export function getPublishedSections(
  page: IHelpCenterAudiencePage,
): IHelpCenterSection[] {
  const sections = Array.isArray(page.sections) ? page.sections : [];

  return sortByOrder(
    sections.filter(
      (section) => section.isPublished && section.title.trim().length > 0,
    ),
  ).map((section) => ({
    ...section,
    links: [],
    subSections: getPublishedSubSections(section),
  }));
}

export function getVisibleSubSections(
  section: IHelpCenterSection,
  expanded: boolean,
): IHelpCenterSubSection[] {
  const subSections = getPublishedSubSections(section);
  const maxVisible = section.maxVisibleLinks ?? DEFAULT_VISIBLE_LINK_COUNT;
  if (expanded || subSections.length <= maxVisible) {
    return subSections;
  }
  return subSections.slice(0, maxVisible);
}

export function shouldShowViewAll(section: IHelpCenterSection): boolean {
  const subSections = getPublishedSubSections(section);
  const maxVisible = section.maxVisibleLinks ?? DEFAULT_VISIBLE_LINK_COUNT;
  return subSections.length > maxVisible;
}

export function filterSectionsByKeyword(
  sections: IHelpCenterSection[],
  query: string,
): IHelpCenterSection[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return sections;
  }

  return sections
    .map((section) => {
      const titleMatches = section.title.toLowerCase().includes(normalized);
      const publishedSubSections = getPublishedSubSections(section);
      const matchingSubSections = publishedSubSections.filter((subSection) =>
        subSection.title.toLowerCase().includes(normalized),
      );

      if (!titleMatches && matchingSubSections.length === 0) {
        return null;
      }

      return {
        ...section,
        subSections: titleMatches ? publishedSubSections : matchingSubSections,
      };
    })
    .filter((section): section is IHelpCenterSection => section !== null);
}

export type HelpCenterSectionEntry = {
  audience: HelpCenterAudience;
  section: IHelpCenterSection;
};

export function buildHelpCenterSectionEntries(
  config: IHelpCenter,
  audiences: HelpCenterAudience[],
): HelpCenterSectionEntry[] {
  return audiences.flatMap((audience) => {
    const page = getAudiencePage(config, audience);
    if (!page) {
      return [];
    }

    return getPublishedSections(page).map((section) => ({
      audience,
      section,
    }));
  });
}

export function filterSectionEntriesByKeyword(
  entries: HelpCenterSectionEntry[],
  query: string,
): HelpCenterSectionEntry[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return entries;
  }

  return entries
    .map((entry) => {
      const filtered = filterSectionsByKeyword([entry.section], query);
      if (filtered.length === 0) {
        return null;
      }

      return {
        audience: entry.audience,
        section: filtered[0],
      };
    })
    .filter((entry): entry is HelpCenterSectionEntry => entry !== null);
}

export function groupSectionEntriesByAudience(
  entries: HelpCenterSectionEntry[],
  audiences: HelpCenterAudience[],
): Array<{ audience: HelpCenterAudience; entries: HelpCenterSectionEntry[] }> {
  return audiences
    .map((audience) => ({
      audience,
      entries: entries.filter((entry) => entry.audience === audience),
    }))
    .filter((group) => group.entries.length > 0);
}

export function sortHelpCenterConfig(config: IHelpCenter): IHelpCenter {
  const normalized = normalizeHelpCenterConfig(config);

  return {
    ...normalized,
    members: {
      ...normalized.members,
      sections: sortByOrder(normalized.members.sections).map((section) => ({
        ...section,
        links: sortByOrder(section.links ?? []),
        subSections: section.subSections ?? [],
      })),
    },
    dataAdmin: {
      ...normalized.dataAdmin,
      sections: sortByOrder(normalized.dataAdmin.sections).map((section) => ({
        ...section,
        links: sortByOrder(section.links ?? []),
        subSections: section.subSections ?? [],
      })),
    },
    staff: {
      ...normalized.staff,
      sections: sortByOrder(normalized.staff.sections).map((section) => ({
        ...section,
        links: sortByOrder(section.links ?? []),
        subSections: section.subSections ?? [],
      })),
    },
  };
}

const DEFAULT_MEMBERS_PAGE: IHelpCenterAudiencePage = {
  title: "WSG Help Center",
  intro:
    "Welcome, WSG Member. This is your dedicated gateway for maximizing your membership. Whether you need step-by-step guides, video tutorials, or technical instructions, everything you need to navigate the WSG platform and leverage your benefits is right here at your fingertips.",
  sections: [],
};

const DEFAULT_DATA_ADMIN_PAGE: IHelpCenterAudiencePage = {
  title: "WSG Help Center",
  intro:
    "Welcome, Data Admin & Contacts. Use the links below to access help guides, downloadable PDFs, videos, and more for managing your firm on the WSG website.",
  sections: [],
};

const DEFAULT_STAFF_PAGE: IHelpCenterAudiencePage = {
  title: "WSG Help Center",
  intro:
    "Welcome, WSG Staff. Use the links below to access internal help guides, downloadable PDFs, videos, and more.",
  sections: [],
};

type LegacyHelpCenterSection = Partial<IHelpCenterSection> & {
  tutorial?: IHelpCenterMediaResource;
  pdfDownload?: IHelpCenterMediaResource;
};

type LegacyHelpCenterPayload = Partial<IHelpCenter> & {
  title?: string;
  intro?: string;
  sections?: LegacyHelpCenterSection[];
};

function normalizeMediaResource(
  value: unknown,
): IHelpCenterMediaResource | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const media = value as Record<string, unknown>;
  const thumbnailUrl = String(media.thumbnailUrl ?? "").trim();
  const mediaUrl = String(media.mediaUrl ?? "").trim();

  if (!thumbnailUrl && !mediaUrl) {
    return undefined;
  }

  return {
    thumbnailUrl: thumbnailUrl || undefined,
    mediaUrl: mediaUrl || undefined,
  };
}

function normalizeSubSection(
  subSection: Partial<IHelpCenterSubSection>,
  subSectionIndex: number,
): IHelpCenterSubSection {
  const steps = Array.isArray(subSection.steps) ? subSection.steps : [];

  return {
    id: String(subSection.id ?? `subsection-${subSectionIndex + 1}`),
    title: String(subSection.title ?? "").trim(),
    sortOrder:
      typeof subSection.sortOrder === "number"
        ? subSection.sortOrder
        : subSectionIndex + 1,
    isPublished: subSection.isPublished !== false,
    steps: steps.map((step, stepIndex) => ({
      id: String(step.id ?? `step-${subSectionIndex + 1}-${stepIndex + 1}`),
      sortOrder:
        typeof step.sortOrder === "number" ? step.sortOrder : stepIndex + 1,
      description: String(step.description ?? "").trim(),
      imageUrl: String(step.imageUrl ?? "").trim() || undefined,
    })),
    tutorial: normalizeMediaResource(subSection.tutorial),
    pdfDownload: normalizeMediaResource(subSection.pdfDownload),
  };
}

function normalizeSection(
  section: LegacyHelpCenterSection,
  sectionIndex: number,
): IHelpCenterSection {
  const links = Array.isArray(section.links) ? section.links : [];
  const legacyTutorial = normalizeMediaResource(section.tutorial);
  const legacyPdf = normalizeMediaResource(section.pdfDownload);
  const subSections = Array.isArray(section.subSections)
    ? section.subSections.map((subSection, index) =>
        normalizeSubSection(subSection, index),
      )
    : [];

  if (subSections.length > 0 && (legacyTutorial || legacyPdf)) {
    subSections[0] = {
      ...subSections[0],
      tutorial: subSections[0].tutorial ?? legacyTutorial,
      pdfDownload: subSections[0].pdfDownload ?? legacyPdf,
    };
  }

  return {
    id: String(section.id ?? `section-${sectionIndex + 1}`),
    title: String(section.title ?? "").trim(),
    description: String(section.description ?? "").trim() || undefined,
    sortOrder:
      typeof section.sortOrder === "number" ? section.sortOrder : sectionIndex + 1,
    isPublished: section.isPublished !== false,
    maxVisibleLinks:
      typeof section.maxVisibleLinks === "number" && section.maxVisibleLinks > 0
        ? section.maxVisibleLinks
        : DEFAULT_VISIBLE_LINK_COUNT,
    links: links.map((link, linkIndex) => ({
      id: String(link.id ?? `link-${sectionIndex + 1}-${linkIndex + 1}`),
      label: String(link.label ?? "").trim(),
      url: String(link.url ?? "").trim(),
      sortOrder:
        typeof link.sortOrder === "number" ? link.sortOrder : linkIndex + 1,
      isPublished: link.isPublished !== false,
      openInNewTab: Boolean(link.openInNewTab),
    })),
    subSections,
  };
}

type NormalizeAudiencePageInput = Partial<
  Omit<IHelpCenterAudiencePage, "sections">
> & {
  sections?: LegacyHelpCenterSection[];
};

function normalizeAudiencePage(
  page: NormalizeAudiencePageInput | undefined,
  fallback: IHelpCenterAudiencePage,
): IHelpCenterAudiencePage {
  if (!page) {
    return fallback;
  }

  const sections = Array.isArray(page.sections)
    ? page.sections.map((section, index) => normalizeSection(section, index))
    : fallback.sections;

  return {
    title: String(page.title ?? fallback.title).trim() || fallback.title,
    intro: String(page.intro ?? fallback.intro).trim() || fallback.intro,
    sections,
  };
}

export function normalizeHelpCenterConfig(raw: unknown): IHelpCenter {
  const payload = (raw ?? {}) as LegacyHelpCenterPayload;

  if (payload.members || payload.dataAdmin || payload.staff) {
    return {
      id: String(payload.id ?? "help-center-config"),
      members: normalizeAudiencePage(payload.members, DEFAULT_MEMBERS_PAGE),
      dataAdmin: normalizeAudiencePage(payload.dataAdmin, DEFAULT_DATA_ADMIN_PAGE),
      staff: normalizeAudiencePage(payload.staff, DEFAULT_STAFF_PAGE),
      updatedAt: payload.updatedAt,
      updatedBy: payload.updatedBy,
    };
  }

  const legacyMembers = normalizeAudiencePage(
    {
      title: payload.title,
      intro: payload.intro,
      sections: payload.sections,
    },
    DEFAULT_MEMBERS_PAGE,
  );

  return {
    id: String(payload.id ?? "help-center-config"),
    members: legacyMembers,
    dataAdmin: DEFAULT_DATA_ADMIN_PAGE,
    staff: DEFAULT_STAFF_PAGE,
    updatedAt: payload.updatedAt,
    updatedBy: payload.updatedBy,
  };
}
